import React, { useEffect, useMemo, useState } from 'react'
import { Button, Card, Col, Form, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import Swal from 'sweetalert2'
import Loader from '../design/Loader/Loader'
import { getActivePassesForTicketPurchase } from '../../actions/ticketing/passDiscountActions'
import { addCartItem } from '../../actions/payments/cartActions'
import useAuth from '../../context/auth/useAuth'

export default function TicketPurchaseWithPassDiscount({ match, club, fees = [] }) {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const { clientauth } = useAuth()
    const addonDisplayName = match?.shirtAddonName || t('matches.shirtAddon', 'Cobro Extra')
    const { commission } = useSelector(state => state.commissions)
    const { items } = useSelector(state => state.cart)
    const { passes, loading, error } = useSelector(state => state.passDiscount)

    const [showPassDiscountForm, setShowPassDiscountForm] = useState(false)
    const [passValidationType, setPassValidationType] = useState(clientauth?.client ? 'logged' : 'passNumber')
    const [passNumber, setPassNumber] = useState('')
    const [email, setEmail] = useState('')
    const [lastname, setLastname] = useState('')
    const [loadingPurchase, setLoadingPurchase] = useState(false)
    const [shirtSelectionByPass, setShirtSelectionByPass] = useState({})

    const hasSubscriptionDiscount = match?.subscriptionDiscount && match?.subscriptionDiscountPercent > 0

    const normalize = (value = '') => String(value)
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()

    const bestAvailableTicketOptionByPass = useMemo(() => {
        const byPass = {}
        const allOptions = []

        if (!match?.sectors?.length) {
            return byPass
        }

        match.sectors.forEach(sector => {
            if (!sector?.active || sector?.deleted || sector?.marketplaceVisible !== true) return

            sector.fees?.forEach(fee => {
                if (!fee?.active) return
                const feeId = fee?.fee?._id || fee?.fee
                const feeName = fees?.find(f => f?._id === feeId || f?._id?.toString() === feeId?.toString())?.name || ''
                allOptions.push({
                    sectorId: sector.sector,
                    feeId,
                    feeName,
                    rawPrice: Number(fee.price || 0)
                })
            })
        })

        const getPassTag = (pass) => {
            const passTypeName = normalize(pass?.passType?.name || '')
            if (passTypeName.includes('especial')) return 'especial'
            // Business rule: if pass type is not "especial", discount applies over GENERAL ticket.
            return 'general'
        }

        const pickCheapest = (options = []) => {
            if (!options.length) return null
            return options.reduce((best, option) => (!best || option.rawPrice < best.rawPrice ? option : best), null)
        }

        ;(passes || []).forEach(pass => {
            const tag = getPassTag(pass)
            const passSectorId = pass?.sector?._id || pass?.sector
            const optionsByTag = allOptions.filter(option => normalize(option.feeName).includes(tag))
            const optionsByTagAndSector = optionsByTag.filter(option => passSectorId && option?.sectorId?.toString() === passSectorId?.toString())
            const optionsBySector = allOptions.filter(option => passSectorId && option?.sectorId?.toString() === passSectorId?.toString())

            const selected =
                pickCheapest(optionsByTagAndSector) ||
                pickCheapest(optionsByTag) ||
                pickCheapest(optionsBySector) ||
                pickCheapest(allOptions)

            byPass[pass?._id] = selected
        })

        return byPass
    }, [match, fees, passes])

    const isPassAlreadyInCart = (passId) => {
        return (items || []).some(item => item.match?._id === match?._id && item.ticket?.client?.pass === passId)
    }

    const getPassCartTicket = (passId) => {
        return (items || []).find(item => item.match?._id === match?._id && item.ticket?.client?.pass === passId)
    }

    const getDiscountValues = (ticketOption) => {
        const originalPrice = Number(ticketOption?.rawPrice || 0)
        const percent = Number(match?.subscriptionDiscountPercent || 0)
        const rawPrice = Math.round((originalPrice * (1 - (percent / 100))) * 100) / 100

        return {
            originalPrice,
            percent,
            rawPrice,
            savings: Math.round((originalPrice - rawPrice) * 100) / 100
        }
    }

    const getShirtRawPrice = (selected) => {
        if (!match?.shirtAddonEnabled || !selected) return 0
        return Number(match?.shirtAddonPriceSubscriber || 0)
    }

    const getPassDisplayName = (pass) => {
        const firstname = pass?.personData?.firstname || pass?.client?.firstname || ''
        const lastname = pass?.personData?.lastname || pass?.client?.lastname || ''
        return `${firstname} ${lastname}`.trim()
    }

    useEffect(() => {
        // Logged users should immediately see their valid passes for this match.
        if (!hasSubscriptionDiscount || !clientauth?.client || !club?._id || !match?._id) return

        setShowPassDiscountForm(true)
        setPassValidationType('logged')
        dispatch(getActivePassesForTicketPurchase({
            clubId: club._id,
            matchId: match._id,
            clientId: clientauth.client
        }))
    }, [hasSubscriptionDiscount, clientauth?.client, club?._id, match?._id, dispatch])

    const handleSearchPasses = async () => {
        if (!club?._id || !match?._id) return

        const params = {
            clubId: club._id,
            matchId: match._id
        }

        // Agregar parámetros según el tipo de validación
        if (passValidationType === 'logged' && clientauth?.client) {
            params.clientId = clientauth.client
        } else if (passValidationType === 'passNumber' && passNumber && email) {
            params.passNumber = passNumber
            params.email = email
        } else if (passValidationType === 'passNumberLastname' && passNumber && lastname) {
            params.passNumber = passNumber
            params.lastname = lastname
        } else {
            Swal.fire({
                icon: 'warning',
                title: t('matches.passValidationTitle', 'Validación de abono'),
                text: t('matches.passValidationIncomplete', 'Por favor, completa los datos para validar tu abono')
            })
            return
        }

        dispatch(getActivePassesForTicketPurchase(params))
    }

    const handlePurchaseTicket = async (pass) => {
        if (!pass || !match || !club) return

        const selectedOption = bestAvailableTicketOptionByPass?.[pass?._id]

        if (!selectedOption) {
            Swal.fire({
                icon: 'error',
                title: t('global.error'),
                text: t('matches.noAvailableDiscountTicket')
            })
            return
        }

        if (pass.alreadyHasTicket || isPassAlreadyInCart(pass._id)) {
            return
        }

        setLoadingPurchase(true)

        try {
            const discount = getDiscountValues(selectedOption)
            const shirtSelected = !!shirtSelectionByPass?.[pass._id]
            const cartItem = {
                match,
                paymentClient: clientauth?.client || pass.paymentClient || pass.client?._id || null,
                commission: commission?.ticketing,
                club,
                ticket: {
                    type: 'passDiscount',
                    client: {
                        pass: pass._id,
                        passType: pass.passType?._id || pass.passType,
                        passTypeName: pass.passType?.name || '',
                        firstname: pass.personData?.firstname || pass.client?.firstname,
                        lastname: pass.personData?.lastname || pass.client?.lastname,
                        name: pass.personData?.firstname || pass.client?.firstname,
                        surnames: pass.personData?.lastname || pass.client?.lastname
                    },
                    firstname: pass.personData?.firstname || pass.client?.firstname,
                    lastname: pass.personData?.lastname || pass.client?.lastname,
                    email: pass.personData?.email || '',
                    phone: pass.personData?.phone || '',
                    birthDate: pass.personData?.birthDate || '',
                    dni: pass.personData?.dni || '',
                    sector: {
                        _id: selectedOption.sectorId,
                        name: pass.sector?.name || ''
                    },
                    fee: {
                        _id: selectedOption.feeId
                    },
                    discount,
                    addon: {
                        shirtSelected,
                        shirtName: match?.shirtAddonName || 'Cobro Extra',
                        shirtRawPrice: getShirtRawPrice(shirtSelected),
                        shirtPriceType: 'subscriber'
                    }
                }
            }

            await dispatch(addCartItem(cartItem))

            Swal.fire({
                icon: 'success',
                title: t('matches.ticketAddedToCartTitle'),
                text: t('matches.ticketAddedToCartText'),
                confirmButtonText: t('global.accept')
            })
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: t('global.error'),
                text: error.response?.data?.message || t('matches.ticketCartError')
            })
        } finally {
            setLoadingPurchase(false)
        }
    }

    if (!hasSubscriptionDiscount) {
        return null
    }

    return (
        <Card className="my-4 border-info">
            <Card.Body>
                <h4 className="d-flex align-items-center mb-3">
                    <i className="fas fa-ticket-alt me-2 text-info"></i>
                    {t('matches.buyWithPassDiscount', 'Compra de entradas para abonados')}
                </h4>
                <p className="text-muted small mb-3">
                    {t('matches.passDiscountDescription', 'Valida tu abono para aplicar descuento en la entrada.')}
                </p>

                {!showPassDiscountForm ? (
                    <Button
                        variant="info"
                        onClick={() => setShowPassDiscountForm(true)}
                        className="w-100"
                    >
                        <i className="fas fa-check-circle me-2"></i>
                        {t('matches.purchaseWithPass', 'Comprar con abono')}
                    </Button>
                ) : (
                    <div>
                        <div className="mb-3">
                            <Form.Label className="fw-bold">{t('matches.selectValidationType', 'Selecciona cómo validar tu abono')}</Form.Label>
                            <Form.Select
                                value={passValidationType}
                                onChange={(e) => setPassValidationType(e.target.value)}
                                className="mb-3"
                            >
                                {clientauth?.client && <option value="logged">{t('matches.loggedInPass', 'Usar mis abonos (sesión iniciada)')}</option>}
                                <option value="passNumber">{t('matches.passNumberEmail', 'Número de abono + email')}</option>
                                <option value="passNumberLastname">{t('matches.passNumberLastname', 'Número de abono + apellido')}</option>
                            </Form.Select>
                        </div>

                        {passValidationType === 'passNumber' && (
                            <Row className="mb-3">
                                <Col xs={12} md={6}>
                                    <Form.Group>
                                        <Form.Control
                                            type="number"
                                            placeholder={t('matches.passNumber', 'Numero de abono')}
                                            value={passNumber}
                                            onChange={(e) => setPassNumber(e.target.value)}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col xs={12} md={6}>
                                    <Form.Group>
                                        <Form.Control
                                            type="email"
                                            placeholder={t('global.email')}
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        )}

                        {passValidationType === 'passNumberLastname' && (
                            <Row className="mb-3">
                                <Col xs={12} md={6}>
                                    <Form.Group>
                                        <Form.Control
                                            type="number"
                                            placeholder={t('matches.passNumber', 'Numero de abono')}
                                            value={passNumber}
                                            onChange={(e) => setPassNumber(e.target.value)}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col xs={12} md={6}>
                                    <Form.Group>
                                        <Form.Control
                                            type="text"
                                            placeholder={t('matches.ticket.lastname')}
                                            value={lastname}
                                            onChange={(e) => setLastname(e.target.value)}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        )}

                        <Row className="mb-3">
                            <Col xs={12}>
                                <Button
                                    variant="primary"
                                    onClick={handleSearchPasses}
                                    disabled={loading}
                                    className="w-100"
                                >
                                    {loading ? <Loader /> : t('matches.searchPass', 'Buscar abono')}
                                </Button>
                            </Col>
                        </Row>

                        {error && (
                            <div className="alert alert-danger mb-3">
                                {error}
                            </div>
                        )}

                        {passes && passes.length > 0 && (
                            <div className="mb-3">
                                <h6 className="fw-bold mb-2">{t('matches.selectPass')}</h6>
                                {passes.map((pass, idx) => (
                                    (() => {
                                        const passCartTicket = getPassCartTicket(pass._id)
                                        const passLocked = pass.alreadyHasTicket || !!passCartTicket
                                        const passShirtChecked = passCartTicket
                                            ? !!passCartTicket?.ticket?.addon?.shirtSelected
                                            : !!shirtSelectionByPass?.[pass._id]
                                        return (
                                    <Card key={idx} className="mb-2 border">
                                        <Card.Body className="p-3">
                                            <Row className="align-items-stretch g-3">
                                                <Col xs={12} md={8} className="d-flex flex-column justify-content-between">
                                                    <div>
                                                        <div className="fw-bold text-dark mb-2" style={{ fontSize: '1rem' }}>
                                                            {t('matches.passNumberLabel')}: {pass.passNumber}
                                                        </div>
                                                        <div className="fw-bold text-dark mb-2" style={{ fontSize: '1.15rem', lineHeight: 1.2 }}>
                                                            {getPassDisplayName(pass)}
                                                        </div>
                                                    </div>
                                                    {match?.shirtAddonEnabled && (
                                                        <div className="mt-3 p-3 rounded-3" style={{ backgroundColor: '#f8f9fa', border: '2px solid #d8dee6', minHeight: '102px' }}>
                                                            <Row className="align-items-center g-3">
                                                                <Col xs={12} md={7}>
                                                                    <div className="fw-bold text-dark mb-2" style={{ fontSize: '1.1rem', lineHeight: 1.2 }}>
                                                                        {addonDisplayName}
                                                                    </div>
                                                                    <div className="fw-bold text-primary" style={{ fontSize: '1.15rem', lineHeight: 1.2 }}>
                                                                        {Number(getShirtRawPrice(true)).toFixed(2)}€
                                                                    </div>
                                                                </Col>
                                                                <Col xs={12} md={5}>
                                                                    <div className="d-flex align-items-center justify-content-md-end justify-content-start gap-2" style={{ minHeight: '100%' }}>
                                                                        <span style={{ fontWeight: !shirtSelectionByPass?.[pass._id] ? 700 : 500, color: !shirtSelectionByPass?.[pass._id] ? '#212529' : '#adb5bd', fontSize: '1rem' }}>{t('matches.no', 'No')}</span>
                                                                        <Form.Check
                                                                            id={`shirt-addon-${pass._id}`}
                                                                            type="switch"
                                                                            className="m-0 d-flex align-items-center"
                                                                            checked={passShirtChecked}
                                                                            onChange={({ target }) => {
                                                                                setShirtSelectionByPass(prev => ({ ...prev, [pass._id]: target.checked }))
                                                                            }}
                                                                            disabled={passLocked}
                                                                            label=""
                                                                        />
                                                                        <span style={{ fontWeight: passShirtChecked ? 700 : 500, color: passShirtChecked ? '#212529' : '#adb5bd', fontSize: '1rem' }}>{t('matches.yes', 'Sí')}</span>
                                                                    </div>
                                                                </Col>
                                                            </Row>
                                                        </div>
                                                    )}
                                                    {passLocked && (
                                                        <span className="d-inline-block mt-3 px-2 py-1 rounded-2" style={{ backgroundColor: '#fff3cd', color: '#664d03', border: '1px solid #ffecb5', fontSize: '0.8rem', fontWeight: 600 }}>
                                                            {t('matches.alreadyHasTicket', 'Ya tienes entrada para este partido')}
                                                        </span>
                                                    )}
                                                </Col>
                                                <Col xs={12} md={4} className="d-flex align-items-end">
                                                    <Button
                                                        variant={passLocked ? 'secondary' : 'success'}
                                                        size="sm"
                                                        onClick={() => handlePurchaseTicket(pass)}
                                                        disabled={passLocked || loadingPurchase}
                                                        className="w-100"
                                                        style={{ minHeight: match?.shirtAddonEnabled ? '102px' : '56px', fontWeight: 700, fontSize: '1rem' }}
                                                    >
                                                        {loadingPurchase ? (
                                                            <Loader />
                                                        ) : (
                                                            <>
                                                                <i className="fas fa-shopping-cart me-1"></i>
                                                                {t('marketplace.matches.addToCart')}
                                                            </>
                                                        )}
                                                    </Button>
                                                </Col>
                                            </Row>
                                        </Card.Body>
                                    </Card>
                                        )
                                    })()
                                ))}
                            </div>
                        )}

                        {passes && passes.length === 0 && !loading && (
                            <div className="alert alert-info">
                                {t('matches.noPassesFound', 'No se encontraron abonos válidos con los datos proporcionados')}
                            </div>
                        )}

                        <Button
                            variant="outline-secondary"
                            onClick={() => {
                                setShowPassDiscountForm(false)
                                setPassNumber('')
                                setEmail('')
                                setLastname('')
                                setShirtSelectionByPass({})
                                dispatch({ type: 'PASS_DISCOUNT_RESET' })
                            }}
                            className="w-100"
                        >
                            {t('global.cancel')}
                        </Button>
                    </div>
                )}
            </Card.Body>
        </Card>
    )
}



